import { useState } from 'react';
import { Button, Modal, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from 'src/hooks/useApiClient';
import { useConfirmAction } from 'src/hooks/useConfirmAction';
import toaster from 'src/utils/toaster';
import { GTMsendEvent } from 'src/utils/trackingGA4';
import { useVehicleMassiveUpdateContext } from './VehicleMassiveUpdateContext';
import {
	BulkDeleteProgress,
	deleteEach,
	splitByDeletable,
} from './vehicleBulkDelete';

export default function VehicleBulkDeleteButton() {
	const { t } = useTranslation();
	const { apiClient } = useApiClient();
	const queryClient = useQueryClient();
	const { confirmAction, isConfirmShown } = useConfirmAction();
	const { ids, stateById, setIds } = useVehicleMassiveUpdateContext();
	const [progress, setProgress] = useState<BulkDeleteProgress | null>(null);

	const handleClick = async () => {
		if (isConfirmShown || progress) return;

		const { deletable, blocked } = splitByDeletable(ids, stateById);

		if (!deletable.length) {
			toaster.warning(t('vehicle_bulk_delete_none_deletable'));
			return;
		}

		const confirmed = await confirmAction({
			title: t('vehicle_bulk_delete_title'),
			message: (
				<>
					<p>
						{t('vehicle_bulk_delete_confirm_deletable', {
							deletable: deletable.length,
						})}
					</p>
					{blocked.length > 0 && (
						<p>
							{t('vehicle_bulk_delete_confirm_blocked', {
								blocked: blocked.length,
							})}
						</p>
					)}
					<p className="fw-bold mb-0">
						{t('vehicle_bulk_delete_irreversible')}
					</p>
				</>
			),
		});

		if (!confirmed) return;

		setProgress({ done: 0, total: deletable.length });

		const outcome = await deleteEach(
			deletable,
			(id) => apiClient.vehicleController.deleteVehicles(id),
			setProgress
		);

		setProgress(null);

		const deleted = outcome.deleted.length;
		const failed = outcome.failed.length;

		if (failed) {
			toaster.error(t('vehicle_bulk_delete_failed', { deleted, failed }));
		} else if (blocked.length) {
			toaster.success(
				t('vehicle_bulk_delete_result', { deleted, skipped: blocked.length })
			);
		} else {
			toaster.success(t('vehicle_bulk_delete_result_all', { deleted }));
		}

		GTMsendEvent({
			event: 'CTA_delete_vehicles',
			evtCat: 'vehicle_list',
			evtAct: 'delete',
			evtLab: String(deleted),
		});

		setIds(outcome.failed);
		queryClient.invalidateQueries({ queryKey: ['platform', 'vehicles'] });
	};

	return (
		<>
			<Button
				variant="danger"
				onClick={handleClick}
				disabled={progress !== null}
			>
				{t('vehicle_bulk_delete_button').toUpperCase()}
			</Button>

			<Modal
				show={progress !== null}
				backdrop="static"
				keyboard={false}
				animation={false}
				centered
			>
				<Modal.Body>
					{progress && (
						<>
							<p>{t('vehicle_bulk_delete_progress', progress)}</p>
							<ProgressBar now={progress.done} max={progress.total} />
						</>
					)}
				</Modal.Body>
			</Modal>
		</>
	);
}
